import { Button, Input, SimpleFormField } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { registrationRefusal } from '@/domains/api';
import { TestInboxField } from '../components/test-inbox-field';
import { useTestInbox } from '../mail-delivery';
import type { IRegistrationCompletion, IRegistrationView } from '../types';
import { DOOR, fieldLabel, unplacedFailure, validationErrors } from './registration-copy';
import { MessageList, StepHeading, slotMessage, useStepAction } from './step-parts';

/**
 * Page 08, "Create your account" — an anonymous person's run's `complete` (SR-487 decision 8):
 * the names, the work email, and on dev and UAT the test inbox. The one step that creates
 * anything: the account, the person, and the invitation to sign in at Invent's IdP, which holds
 * the password — so none is asked for.
 *
 * **The fields are the view's** (`legalFirstName`, `legalLastName`), named in the wireframe's
 * words and marked required as it marks them. Nothing here checks them: survey-api needs a name to
 * compose the person's from and says so per field, and its refusals land on the field they name —
 * `address_unavailable` on Work email, which leaves the run open for another address. The names
 * start empty: the view carries no split name to start them from.
 *
 * No way back: the wireframe has none, and the numbers it would go back to are settled.
 */
export function CreateAccountStep({
  view,
  onComplete
}: {
  view: IRegistrationView;
  onComplete: (completion: IRegistrationCompletion) => Promise<void>;
}) {
  const [fields, setFields] = React.useState<Record<string, string>>({});
  const [email, setEmail] = React.useState('');
  const { pending, error, run } = useStepAction();
  const inbox = useTestInbox();

  const problems = validationErrors(error);
  const addressRefused = registrationRefusal(error) === 'address_unavailable';
  const emailError = addressRefused
    ? 'That address cannot be used. Try a different one.'
    : slotMessage(problems.email);
  // `deliverTo` is placed by the test inbox, which says its own refusals.
  const placed = new Set([...view.fields, 'email', 'deliverTo']);
  const failure = unplacedFailure(
    addressRefused ? null : error,
    placed,
    'Could not create your account.'
  );

  return (
    <>
      <StepHeading title={DOOR.create.title} lede={DOOR.create.lede} />

      <form
        noValidate
        className="grid gap-5"
        onSubmit={(event) => {
          event.preventDefault();
          inbox.begin();
          void run(async () => {
            try {
              await onComplete({ email, fields, ...inbox.body });
            } catch (refusal) {
              if (!inbox.refused(refusal, email)) throw refusal;
            }
          });
        }}
      >
        <div className="grid gap-5 sm:grid-cols-2 sm:gap-3">
          {view.fields.map((name) => {
            const id = `registration-field-${name}`;
            return (
              <SimpleFormField
                key={name}
                label={fieldLabel(name)}
                htmlFor={id}
                required
                error={slotMessage(problems[name])}
              >
                <Input
                  id={id}
                  value={fields[name] ?? ''}
                  onChange={(event) =>
                    setFields((current) => ({ ...current, [name]: event.target.value }))
                  }
                  aria-invalid={!!problems[name]}
                />
                <MessageList messages={problems[name]} />
              </SimpleFormField>
            );
          })}
        </div>

        <SimpleFormField
          label={DOOR.create.email}
          htmlFor="registration-email"
          required
          error={emailError}
        >
          {/* No format rule: survey-api judges an address's shape itself. */}
          <Input
            id="registration-email"
            type="text"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            aria-invalid={addressRefused || !!problems.email}
          />
          <MessageList messages={problems.email} />
        </SimpleFormField>

        <TestInboxField
          inbox={inbox}
          id="registration-deliver-to"
          intended={email}
          disabled={pending}
          whenEmpty={
            <>
              Left empty, the account is still created, and the invitation to sign in reaches{' '}
              {email.trim() ? <strong className="break-words">{email.trim()}</strong> : 'it'}{' '}
              only if it ends in one of them.
            </>
          }
        />

        {failure && (
          <p role="alert" className="text-sm font-medium text-destructive">
            {failure}
          </p>
        )}

        <Button type="submit" disabled={pending || !inbox.settled} className="w-full">
          {pending ? DOOR.create.busy : DOOR.create.submit}
        </Button>
      </form>
    </>
  );
}
